
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, User } from "lucide-react";
import { TimeSlot, useDeleteTimeSlot, useUpdateTimeSlot } from "@/hooks/useTimeSlots";
import { format } from "date-fns";

interface TimeSlotTableRowProps {
  timeSlot: TimeSlot;
  onEdit?: (timeSlot: TimeSlot) => void;
}

// Female gender symbol component
const FemaleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <circle cx="12" cy="8" r="6" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M12 14v6" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 19h6" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const TimeSlotTableRow = ({ timeSlot, onEdit }: TimeSlotTableRowProps) => {
  const deleteTimeSlot = useDeleteTimeSlot();
  const updateTimeSlot = useUpdateTimeSlot();

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this time slot?")) {
      deleteTimeSlot.mutate(timeSlot.id);
    }
  };

  const handleEdit = () => {
    onEdit?.(timeSlot);
  };

  const toggleAvailability = () => {
    updateTimeSlot.mutate({
      id: timeSlot.id,
      is_available: !timeSlot.is_available,
    });
  };

  const toggleWomensOnly = () => {
    updateTimeSlot.mutate({
      id: timeSlot.id,
      womens_only: !timeSlot.womens_only,
    });
  };

  const startTime = new Date(timeSlot.start_time);
  const endTime = new Date(timeSlot.end_time);
  const isFullyBooked = timeSlot.current_bookings >= timeSlot.max_bookings;
  const isPast = startTime < new Date();

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="text-sm sm:text-base">
          {format(startTime, "MMM dd, yyyy")}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm sm:text-base">
          {format(startTime, "HH:mm")} - {format(endTime, "HH:mm")}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="text-sm sm:text-base">
            {timeSlot.service?.name}
          </div>
          {timeSlot.womens_only && (
            <FemaleIcon className="h-4 w-4 text-pink-500" />
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm sm:text-base">
          {timeSlot.room?.name || "N/A"}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm sm:text-base">
          {timeSlot.current_bookings} / {timeSlot.max_bookings}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge 
            variant={timeSlot.is_available && !isPast ? "default" : "secondary"}
            className="text-xs"
          >
            {isPast ? "Past" : timeSlot.is_available ? "Available" : "Unavailable"}
          </Badge>
          {isFullyBooked && (
            <Badge variant="destructive" className="text-xs">
              Full
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAvailability}
            disabled={isPast}
            className="text-xs px-2 py-1"
          >
            {timeSlot.is_available ? "Disable" : "Enable"}
          </Button>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleWomensOnly}
              disabled={isPast || updateTimeSlot.isPending}
              className={`px-2 py-1 ${
                timeSlot.womens_only 
                  ? 'bg-pink-500 text-white border-pink-500 hover:bg-pink-600' 
                  : 'border-pink-300 text-pink-600 hover:bg-pink-50'
              }`}
              title={timeSlot.womens_only ? "Remove women-only restriction" : "Make women-only"}
            >
              <User className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              disabled={isPast}
              className="px-2 py-1"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteTimeSlot.isPending}
              className="px-2 py-1"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default TimeSlotTableRow;
